import React, { useState, useEffect, useRef } from 'react';
import { 
  sendArriveOrigin, 
  sendArriveDestination,
  sendLoad, 
  sendComplete, 
  sendNoShow,
  sendStartBreak,
  sendCompleteBreak,
  sendCompleteSplitShiftBreak,
  sendCompleteStart,
  sendCompleteEnd,
  sendSignature,
  sendCopayConfirm
} from './itinerarySocketHelper';
import PickupArrivalModal from './PickupArrivalModal';
import { startNativeNavigation } from '../../../utils/nativeNavigation';
import {
  resolveTripPaidBinary,
  resolveTripPaymentStatus,
  mergePaymentOverride,
  TRIP_PAYMENT_STATE,
} from '../../../utils/tripPaymentStatus';

/**
 * JUNE 2026: Compute the seconds remaining on a break independent of schedule.
 * The countdown must run the full configured duration from the moment the driver
 * actually started the break (actualDateTime), and resume correctly after a
 * reconnect. Once steps are ordered by Sequence the driver can reach a break
 * early/late vs its scheduled time, so anchoring to EndDateTime alone produced a
 * partial countdown (e.g. 13 min instead of 30). Priority:
 *   1. actualDateTime + breakTime (actual start + duration)  -- resume-aware
 *   2. server EndDateTime - now                              -- fallback
 */
const computeBreakRemainingSeconds = (wp) => {
  if (!wp) return 0;
  const now = Date.now();
  const durationSecs = typeof wp.breakTime === 'number' && wp.breakTime > 0 ? wp.breakTime : null;
  if (wp.actualDateTime && durationSecs) {
    const startMs = new Date(wp.actualDateTime).getTime();
    if (!isNaN(startMs)) {
      return Math.max(0, Math.floor((startMs + durationSecs * 1000 - now) / 1000));
    }
  }
  if (wp.EndDateTime) {
    const endMs = new Date(wp.EndDateTime).getTime();
    if (!isNaN(endMs)) {
      return Math.max(0, Math.floor((endMs - now) / 1000));
    }
  }
  return 0;
};

const ItineraryCurrentWaypoint = ({ 
  waypoint, 
  /** True once trip:paymentSettled arrived for this trip — flips the badge to Paid
   *  live, without waiting for a route reload. */
  tripPaidLive = false, 
  /** Live settlement for this trip (ItineraryDispatch): { paid, paymentAmount, paymentMethod }. */
  tripPaymentOverride = null,
  /** True when a settlement arrived that the server could not reconcile. */
  tripNeedsReview = false,
  /** Opens the shared Collect Payment sheet; (waypoint, 'endTrip') from Complete. */
  onOpenPayment = null,
  expectedTime, 
  isAccurate, 
  isPhone, 
  currentLocation, 
  onShiftComplete, 
  activeBreak,
  // Demand Response phase props
  demandResponseMode = false,
  currentPhase,
  setCurrentPhase,
  outboundPassengerCount,
  setOutboundPassengerCount,
  baseLat,
  baseLon,
  lastHubDepartureTime,
  setLastHubDepartureTime,
  currentBlockTrips = [],
  onManualHubArrival,
  onCompleteAllLoadedTrips,
  isCompletingArrivals = false,
  onStartOutbound,
  onConfirmBoarding,
  onOpenFlagTripModal,
  // Temporary pickup mode props
  temporaryPickupMode = false,
  onTemporaryPickupComplete = null
}) => {
  // Demand Response boarding input state
  const [boardingCount, setBoardingCount] = useState(0);
  
  // Initialize arrival state from server's StepStatus or isArrived property


  const [isArrivedOrigin, setIsArrivedOrigin] = useState(() => {
    // For pickup steps, check StepStatus first, then isArrived
    const isArrivedFromServer = waypoint?.StepStatus === 'arrived' || waypoint?.isArrived || false;
    return waypoint?.type === 'pickup' ? isArrivedFromServer : false;
  });
  const [isArrivedDestination, setIsArrivedDestination] = useState(() => {
    // For delivery steps, check StepStatus first, then isArrived
    const isArrivedFromServer = waypoint?.StepStatus === 'arrived' || waypoint?.isArrived || false;
    return waypoint?.type === 'delivery' || waypoint?.type === 'dropoff' ? isArrivedFromServer : false;
  });
  // Auto-show pickup modal if waypoint is pickup and already arrived
  const [showPickupArrivalModal, setShowPickupArrivalModal] = useState(() => {
    const isArrivedFromServer = waypoint?.StepStatus === 'arrived' || waypoint?.isArrived || false;
    return waypoint?.type === 'pickup' && isArrivedFromServer;
  });
  
  // Track the last TripId for which we manually closed the modal (Load/NoShow)
  // This prevents auto-opening modal for a new trip until Arrive is pressed
  const [lastProcessedTripId, setLastProcessedTripId] = useState(null);
  
  // Track if user manually closed the modal without taking action (Cancel button)
  // This ensures "Arrive" button is shown which re-opens modal without re-sending arrive status
  const [modalClosedWithoutAction, setModalClosedWithoutAction] = useState(false);

  // Break state management - initialize from server status if break step is already arrived
  const [isOnBreak, setIsOnBreak] = useState(() => {
    const isBreakStep = waypoint?.breakStep || waypoint?.isBreak || (waypoint?.type && waypoint?.type.toLowerCase() === 'break');
    const isArrivedFromServer = waypoint?.StepStatus === 'arrived' || waypoint?.isArrived || false;
    return isBreakStep && isArrivedFromServer;
  });
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(() => {
    // If break is already started (arrived), calculate remaining time from actual start + duration
    const isBreakStep = waypoint?.breakStep || waypoint?.isBreak || (waypoint?.type && waypoint?.type.toLowerCase() === 'break');
    const isArrivedFromServer = waypoint?.StepStatus === 'arrived' || waypoint?.isArrived || false;
    if (isBreakStep && isArrivedFromServer) {
      return computeBreakRemainingSeconds(waypoint);
    }
    return 0;
  });
  const breakTimerRef = useRef(null);
  
  // Active break overlay state (when break is in progress but current waypoint is not the break step)
  const [activeBreakTimeRemaining, setActiveBreakTimeRemaining] = useState(0);
  const activeBreakTimerRef = useRef(null);

  // Update arrival state when waypoint changes (server update)
  useEffect(() => {
    if (waypoint) {
      // Check StepStatus field for server-side status updates
      const isArrivedFromServer = waypoint.StepStatus === 'arrived' || waypoint.isArrived || false;
      
      if (waypoint.type === 'pickup') {
        setIsArrivedOrigin(isArrivedFromServer);
        // Only auto-show pickup modal if:
        // 1. Already arrived from server
        // 2. Not currently showing
        // 3. This is NOT a new trip after we just processed one (lastProcessedTripId check)
        // 4. User has NOT manually closed the modal without action (modalClosedWithoutAction check)
        if (isArrivedFromServer && !showPickupArrivalModal && waypoint.TripId !== lastProcessedTripId && !modalClosedWithoutAction) {
          setShowPickupArrivalModal(true);
        }
      } else if (waypoint.type === 'delivery' || waypoint.type === 'dropoff') {
        setIsArrivedDestination(isArrivedFromServer);
        // Close pickup modal when moving to delivery step
        setShowPickupArrivalModal(false);
      }
    }
  }, [waypoint, waypoint?.StepStatus, waypoint?.isArrived, waypoint?.type, waypoint?.TripId, showPickupArrivalModal, lastProcessedTripId, modalClosedWithoutAction]);

  // Break timer effect - countdown and auto-complete when time expires
  useEffect(() => {
    if (isOnBreak && breakTimeRemaining > 0) {
      breakTimerRef.current = setInterval(() => {
        setBreakTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up - clear interval (auto-complete handled by separate effect)
            clearInterval(breakTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (breakTimerRef.current) {
        clearInterval(breakTimerRef.current);
      }
    };
  }, [isOnBreak]);

  // Auto-complete break when timer reaches 0
  useEffect(() => {
    if (isOnBreak && breakTimeRemaining === 0 && waypoint?.StepId && currentLocation) {
      setIsOnBreak(false);
      sendCompleteBreak(
        { 
          StepId: waypoint.StepId, 
          vehicleId: waypoint.vehicleId, 
          routeId: waypoint.routeId 
        }, 
        currentLocation
      );
    }
  }, [breakTimeRemaining, isOnBreak, waypoint?.StepId, waypoint?.vehicleId, waypoint?.routeId, currentLocation]);

  // Update break state when waypoint changes or when break step arrives
  useEffect(() => {
    const isBreakStep = waypoint?.breakStep || waypoint?.isBreak || (waypoint?.type && waypoint?.type.toLowerCase() === 'break');
    const isArrivedFromServer = waypoint?.StepStatus === 'arrived' || waypoint?.isArrived || false;
    
    if (isBreakStep && isArrivedFromServer) {
      // Break step is already arrived - set toggle to on and calculate remaining time
      setIsOnBreak(true);
      setBreakTimeRemaining(computeBreakRemainingSeconds(waypoint));
    } else if (!isBreakStep) {
      // Non-break step - reset break state
      setIsOnBreak(false);
      setBreakTimeRemaining(0);
      if (breakTimerRef.current) {
        clearInterval(breakTimerRef.current);
      }
    }
  }, [waypoint?.StepId, waypoint?.StepStatus, waypoint?.isArrived]);

  // Auto-complete break when the actual break duration has elapsed
  useEffect(() => {
    const isBreakStep = waypoint?.breakStep || waypoint?.isBreak || (waypoint?.type && waypoint?.type.toLowerCase() === 'break');
    
    if (isBreakStep && isOnBreak && waypoint?.StepId && currentLocation) {
      const remaining = computeBreakRemainingSeconds(waypoint);
      // Only auto-complete when we have a real elapsed-duration signal (actual start + duration).
      const hasActualStart = !!waypoint?.actualDateTime;
      if (hasActualStart && remaining <= 0) {
        // Break duration has elapsed - auto-complete the break
        setIsOnBreak(false);
        setBreakTimeRemaining(0);
        if (breakTimerRef.current) {
          clearInterval(breakTimerRef.current);
        }
        sendCompleteBreak(
          { 
            StepId: waypoint.StepId, 
            vehicleId: waypoint.vehicleId, 
            routeId: waypoint.routeId 
          }, 
          currentLocation
        );
      }
    }
  }, [waypoint?.StepId, waypoint?.EndDateTime, waypoint?.actualDateTime, waypoint?.vehicleId, waypoint?.routeId, isOnBreak, currentLocation]);

  // Active break overlay timer - tracks break in progress when current waypoint is NOT the break step
  useEffect(() => {
    // Clear any existing timer
    if (activeBreakTimerRef.current) {
      clearInterval(activeBreakTimerRef.current);
      activeBreakTimerRef.current = null;
    }

    // If there's an active break, start countdown from its actual start + duration
    if (activeBreak && (activeBreak.actualDateTime || activeBreak.EndDateTime)) {
      const calculateRemaining = () => computeBreakRemainingSeconds(activeBreak);

      // Set initial value
      setActiveBreakTimeRemaining(calculateRemaining());

      // Start countdown timer
      activeBreakTimerRef.current = setInterval(() => {
        const remaining = calculateRemaining();
        setActiveBreakTimeRemaining(remaining);
        
        if (remaining <= 0) {
          clearInterval(activeBreakTimerRef.current);
          activeBreakTimerRef.current = null;
        }
      }, 1000);
    } else {
      setActiveBreakTimeRemaining(0);
    }

    return () => {
      if (activeBreakTimerRef.current) {
        clearInterval(activeBreakTimerRef.current);
      }
    };
  }, [activeBreak, activeBreak?.EndDateTime, activeBreak?.actualDateTime, activeBreak?.StepId]);

  if (!waypoint) return <h2 style={{textAlign:'center'}} className="w-full">No Assigned Trips</h2>;

  const handleArriveOrigin = () => {
    if (waypoint && waypoint.TripId) {
      // Check if already arrived (user closed modal without action and clicked Arrive again)
      const alreadyArrived = isArrivedOrigin || waypoint.StepStatus === 'arrived' || waypoint.isArrived;
      
      if (!alreadyArrived) {
        // First time arriving - send arrive status to server
        setIsArrivedOrigin(true);
        sendArriveOrigin({ tripId: waypoint.TripId, vehicleId: waypoint.vehicleId, routeId: waypoint.routeId });
      }
      
      // Clear tracking states and show modal
      setLastProcessedTripId(null);
      setModalClosedWithoutAction(false);
      setShowPickupArrivalModal(true);
    }
  };
  
  // Handle modal cancel - user closed without Load/NoShow
  const handleModalCancel = () => {
    setModalClosedWithoutAction(true);
    setShowPickupArrivalModal(false);
  };

  const handleArriveDestination = () => {
    if (waypoint && waypoint.TripId) {
      setIsArrivedDestination(true);
      sendArriveDestination({ tripId: waypoint.TripId, vehicleId: waypoint.vehicleId, routeId: waypoint.routeId });
    }
  };

  const handleLoad = () => {
    if (waypoint && waypoint.TripId) {
      // Close modal immediately (optimistic)
      setShowPickupArrivalModal(false);
      // Mark this trip as processed so modal won't auto-open for next trip
      setLastProcessedTripId(waypoint.TripId);
      // Clear manual close tracking
      setModalClosedWithoutAction(false);
      // Send to server
      sendLoad({ tripId: waypoint.TripId, vehicleId: waypoint.vehicleId, routeId: waypoint.routeId });
      setIsArrivedOrigin(false);
      
      // If in temporary pickup mode, return to outbound mode after load
      if (temporaryPickupMode && onTemporaryPickupComplete) {
        console.log('Temporary pickup loaded, returning to outbound mode');
        // Small delay to let the server process the load action
        setTimeout(() => {
          onTemporaryPickupComplete();
        }, 500);
      }
    } else {
      console.error('Waypoint or trip ID is missing.');
    }
  };

  const handleComplete = () => {
    if (!(waypoint && waypoint.TripId)) {
      console.error('Waypoint or trip number is missing.');
      return;
    }
    // Payment gate (PAYMENT_DRIVERAPP_HANDOFF.md §6–7): a final, payment-required and
    // unsettled fare goes through the Collect Payment sheet, and a METERED trip whose
    // fare dispatch has not accepted yet goes through the final-fare step (PRICE →
    // PRICE|Ok) first. ItineraryDispatch sends the itinerary `complete` action once
    // the trip is Paid (or needs no collection). Paid, account-billed and no-charge
    // fixed-price trips keep the direct action and let the server's completion guard
    // answer. Never COMPM here.
    const meteredNotFinal =
      cardPaymentBlock?.metered === true &&
      cardPaymentStatus.state === TRIP_PAYMENT_STATE.FARE_NOT_FINAL;
    if (
      onOpenPayment &&
      (meteredNotFinal ||
        cardPaymentStatus.state === TRIP_PAYMENT_STATE.UNPAID ||
        cardPaymentStatus.state === TRIP_PAYMENT_STATE.PENDING)
    ) {
      onOpenPayment(waypoint, 'endTrip');
      return;
    }
    sendComplete({ tripId: waypoint.TripId, vehicleId: waypoint.vehicleId, routeId: waypoint.routeId });
  };

  const handleNoShow = () => {
    if (waypoint && waypoint.TripId) {
      // Close modal immediately (optimistic)
      setShowPickupArrivalModal(false);
      // Mark this trip as processed so modal won't auto-open for next trip
      setLastProcessedTripId(waypoint.TripId);
      // Clear manual close tracking
      setModalClosedWithoutAction(false);
      // Send to server
      sendNoShow({ tripId: waypoint.TripId, vehicleId: waypoint.vehicleId, routeId: waypoint.routeId });
      
      // If in temporary pickup mode, return to outbound mode after noshow
      if (temporaryPickupMode && onTemporaryPickupComplete) {
        console.log('Temporary pickup no-showed, returning to outbound mode');
        // Small delay to let the server process the noshow action
        setTimeout(() => {
          onTemporaryPickupComplete();
        }, 500);
      }
    } else {
      console.error('Waypoint or trip number is missing.');
    }
  };

  const handleMapDirections = () => {
    if (!(waypoint && waypoint.Latitude && waypoint.Longitude)) {
      console.error('Cannot open map directions - waypoint missing GPS coordinates');
      return;
    }
    const lat = waypoint.Latitude;
    const lng = waypoint.Longitude;
    const label = waypoint.passengerName || waypoint.address || (waypoint.type === 'pickup' ? 'Pickup' : 'Dropoff');

    // Inside the Android driver app's WebView this launches Google Maps turn-by-turn
    // directly (see docs/Native Navigation Bridge). Outside it (regular browser,
    // local dev) startNativeNavigation no-ops and returns false — fall back to the
    // universal web deep link, which works on both iOS and Android browsers.
    if (startNativeNavigation(lat, lng, '', true, label)) return;

    // Use universal deep link that works on iOS (Apple Maps/Google Maps) and Android
    // (Google Maps). Format: https://www.google.com/maps/dir/?api=1&destination=lat,lng
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(mapsUrl, '_blank');
  };

  // Returns the sendSignature promise so PickupArrivalModal can await the server ack
  // before it unlocks Pickup. Rejects (rather than only logging) when the trip is
  // unidentifiable, otherwise the modal would treat a dropped signature as saved.
  const handleSignatureSave = (signatureData) => {
    if (waypoint && waypoint.TripId) {
      return sendSignature(
        {
          tripId: waypoint.TripId,
          vehicleId: waypoint.vehicleId,
          routeId: waypoint.routeId
        },
        signatureData
      );
    }

    console.error('Waypoint or trip ID is missing.');
    return Promise.reject(new Error('Cannot save signature: trip ID is missing.'));
  };

  const handleCopayConfirm = (amount) => {
    if (waypoint && waypoint.TripId) {
      sendCopayConfirm(
        { 
          tripId: waypoint.TripId, 
          vehicleId: waypoint.vehicleId, 
          routeId: waypoint.routeId 
        }, 
        amount
      );
    } else {
      console.error('Waypoint or trip ID is missing.');
    }
  };

  const handleBreakToggle = (turnOn) => {
    // Validate all required data before sending
    if (!waypoint || !waypoint.StepId) {
      console.error('Cannot toggle break - missing step data');
      alert('Cannot toggle break: Missing step information. Please refresh the app.');
      return;
    }
    if (!currentLocation) {
      console.error('Cannot toggle break - missing GPS location');
      alert('Cannot toggle break: GPS location not available. Please enable location services.');
      return;
    }
    if (!waypoint.vehicleId) {
      console.error('Cannot toggle break - missing vehicleId', { waypoint });
      alert('Cannot toggle break: Vehicle information not available. Please log out and back in.');
      return;
    }
    if (!waypoint.routeId) {
      console.error('Cannot toggle break - missing routeId', { waypoint });
      alert('Cannot toggle break: Route information not available. Please refresh the app.');
      return;
    }

    if (turnOn) {
      // Start break
      setIsOnBreak(true);
      // Set break duration from waypoint (in seconds)
      const breakDuration = waypoint.duration || waypoint.Duration || 1800; // Default 30 min
      setBreakTimeRemaining(breakDuration);
      
      sendStartBreak(
        { 
          StepId: waypoint.StepId, 
          vehicleId: waypoint.vehicleId, 
          routeId: waypoint.routeId 
        }, 
        currentLocation
      );
    } else {
      // End break
      setIsOnBreak(false);
      setBreakTimeRemaining(0);
      if (breakTimerRef.current) {
        clearInterval(breakTimerRef.current);
      }
      
      sendCompleteBreak(
        { 
          StepId: waypoint.StepId, 
          vehicleId: waypoint.vehicleId, 
          routeId: waypoint.routeId 
        }, 
        currentLocation
      );
    }
  };

  const handleCompleteStart = () => {
    // Validate all required data before sending
    if (!waypoint || !waypoint.StepId) {
      console.error('Cannot begin work - missing step data');
      alert('Cannot begin work: Missing step information. Please refresh the app.');
      return;
    }
    if (!currentLocation) {
      console.error('Cannot begin work - missing GPS location');
      alert('Cannot begin work: GPS location not available. Please enable location services.');
      return;
    }
    if (!waypoint.vehicleId) {
      console.error('Cannot begin work - missing vehicleId', { waypoint });
      alert('Cannot begin work: Vehicle information not available. Please log out and back in.');
      return;
    }
    if (!waypoint.routeId) {
      console.error('Cannot begin work - missing routeId', { waypoint });
      alert('Cannot begin work: Route information not available. Please refresh the app.');
      return;
    }
    
    // Get driver ID from localStorage for vehicle assignment
    const userSettings = JSON.parse(localStorage.getItem('userSettings'));
    const driverId = userSettings?.Id;
    
    sendCompleteStart(
      { 
        StepId: waypoint.StepId, 
        vehicleId: waypoint.vehicleId, 
        routeId: waypoint.routeId,
        driverId: driverId  // Pass driverId for vehicle assignment
      }, 
      currentLocation
    );
  };

  const handleCompleteEnd = () => {
    // Validate all required data before sending
    if (!waypoint || !waypoint.StepId) {
      console.error('Cannot complete end - missing step data');
      alert('Cannot complete end: Missing step information. Please refresh the app.');
      return;
    }
    if (!currentLocation) {
      console.error('Cannot complete end - missing GPS location');
      alert('Cannot complete end: GPS location not available. Please enable location services.');
      return;
    }
    if (!waypoint.vehicleId) {
      console.error('Cannot complete end - missing vehicleId', { waypoint });
      alert('Cannot complete end: Vehicle information not available. Please log out and back in.');
      return;
    }
    if (!waypoint.routeId) {
      console.error('Cannot complete end - missing routeId', { waypoint });
      alert('Cannot complete end: Route information not available. Please refresh the app.');
      return;
    }
    
    sendCompleteEnd(
      { 
        StepId: waypoint.StepId, 
        vehicleId: waypoint.vehicleId, 
        routeId: waypoint.routeId 
      }, 
      currentLocation
    );
    
    // Navigate to CompleteShift component after completing end step
    if (onShiftComplete && typeof onShiftComplete === 'function') {
      setTimeout(() => {
        onShiftComplete();
      }, 500); // Small delay to allow server update to complete
    }
  };

  const handleCompleteSplitShiftBreak = () => {
    // Validate all required data before sending
    if (!waypoint || !waypoint.StepId) {
      console.error('Cannot complete split shift - missing step data');
      alert('Cannot complete split shift: Missing step information. Please refresh the app.');
      return;
    }
    if (!currentLocation) {
      console.error('Cannot complete split shift - missing GPS location');
      alert('Cannot complete split shift: GPS location not available. Please enable location services.');
      return;
    }
    if (!waypoint.vehicleId) {
      console.error('Cannot complete split shift - missing vehicleId', { waypoint });
      alert('Cannot complete split shift: Vehicle information not available. Please log out and back in.');
      return;
    }
    if (!waypoint.routeId) {
      console.error('Cannot complete split shift - missing routeId', { waypoint });
      alert('Cannot complete split shift: Route information not available. Please refresh the app.');
      return;
    }
    
    sendCompleteSplitShiftBreak(
      { 
        StepId: waypoint.StepId, 
        vehicleId: waypoint.vehicleId, 
        routeId: waypoint.routeId 
      }, 
      currentLocation
    );
    
    // Navigate to CompleteShift component - driver is "clocking out" for split shift
    // When they re-authorize, they'll resume the same route with progress preserved
    if (onShiftComplete && typeof onShiftComplete === 'function') {
      setTimeout(() => {
        onShiftComplete();
      }, 500);
    }
  };

  // Check step types
  const isBreak = waypoint.breakStep || waypoint.isBreak || (waypoint.type && waypoint.type.toLowerCase() === 'break');
  const isSplitShiftBreak = isBreak && (waypoint.Status === 'splitShift' || waypoint.StepStatus === 'splitShift');
  // Split shift request status checks
  const isSplitShiftRequested = isBreak && (waypoint.Status === 'splitShiftRequested' || waypoint.StepStatus === 'splitShiftRequested');
  const isSplitShiftApproved = isBreak && (waypoint.Status === 'splitShiftApproved' || waypoint.StepStatus === 'splitShiftApproved');
  const isSplitShiftRejected = isBreak && (waypoint.Status === 'splitShiftRejected' || waypoint.StepStatus === 'splitShiftRejected');
  const isStartStep = waypoint.beginStep || (waypoint.type && waypoint.type.toLowerCase() === 'start');
  const isEndStep = waypoint.endStep || (waypoint.type && waypoint.type.toLowerCase() === 'end');
  const isHubStep = waypoint.type && waypoint.type.toLowerCase() === 'hub';
  const scheduledTime = waypoint.type === 'pickup' ? waypoint.trip?.pickup?.date : waypoint.trip?.dropoff?.date;
  const formattedScheduledTime = scheduledTime ? new Date(scheduledTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'N/A';
  const etaTime= new Date(waypoint.eta);

  // Paid/Unpaid for the badge on this card. The server's payment block rides on
  // trip.reservation (db/util.js formatTrip) — paid, paymentStatus, accountBilled,
  // paymentRequired, priceFinal, paymentPending — and BOTH assigned and flag trips go
  // through that same formatter, so nothing server-side is needed. Falls back to
  // 'Unpaid' when the block is absent rather than rendering no badge, since the card
  // must always show one of the two values.
  // Live settlements (trip:paymentSettled, acknowledged cash) are merged over the
  // reservation block so the badge, the gate below and the payment sheet all agree —
  // keyed by this trip's id, so a push for another trip on the route never marks it.
  const cardReservation = waypoint.trip?.reservation || null;
  const cardPaymentBlock = mergePaymentOverride(cardReservation, tripPaymentOverride);
  const cardSettledLive = tripPaidLive === true || tripPaymentOverride?.paid === true;
  const cardPaymentStatus = resolveTripPaymentStatus(cardPaymentBlock, {
    settledLive: cardSettledLive,
    needsReconciliation: tripNeedsReview === true,
  });
  const tripPaidStatus = resolveTripPaidBinary(cardPaymentBlock, {
    settledLive: cardSettledLive,
    needsReconciliation: tripNeedsReview === true,
  });

  // An account-billed trip collects nothing at the door, so "Paid" is misleading there —
  // label it "Account Trip" instead, matching PickupArrivalModal. Same precedence the
  // server uses in paymentAPI.summarizeTrip(): accountBilled is checked before paid.
  const cardHasPaymentBlock = !!cardReservation && (
    cardReservation.accountBilled !== undefined || cardReservation.paid !== undefined ||
    cardReservation.paymentStatus !== undefined || cardReservation.paymentRequired !== undefined
  );
  const isCardAccountTrip = cardHasPaymentBlock &&
    resolveTripPaymentStatus(cardReservation).state === TRIP_PAYMENT_STATE.ACCOUNT_BILLED;

  const comments = waypoint.type === 'pickup' ? waypoint.trip.pickup.comments || '' :  '';
  //add WaitingSecs to etaTime
  if (waypoint.WaitingSecs) {
    etaTime.setSeconds(etaTime.getSeconds() + waypoint.WaitingSecs);
  }
  
  
  return (
    <div className='w-full flex-col overflow-hidden relative'
      style={{
        cursor: 'pointer',
        borderRadius: '0px 0px 5px 5px',
        borderTop: '1px solid lightgray',
        height: !isPhone ? '320px' : 'auto',
      }}>

      {/* Active Break Overlay - shows over bottom portion when break is in progress */}
      {activeBreak && !isBreak && (
        <div 
          className='absolute bottom-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3'
          style={{ 
            backgroundColor: activeBreakTimeRemaining > 0 ? 'rgba(59, 130, 246, 0.95)' : 'rgba(239, 68, 68, 0.95)',
            borderRadius: '0px 0px 5px 5px'
          }}
        >
          <div className='text-white'>
            {activeBreakTimeRemaining > 0 ? (
              <span className='font-bold text-lg'>
                On Break: {Math.floor(activeBreakTimeRemaining / 60)}:{String(activeBreakTimeRemaining % 60).padStart(2, '0')} remaining
              </span>
            ) : (
              <span className='font-bold text-lg'>Break Time Ended</span>
            )}
          </div>
          <button 
            className={`font-bold py-1.5 px-4 rounded text-sm ${activeBreakTimeRemaining > 0 ? 'bg-white text-blue-600' : 'bg-white text-red-600'}`}
            onClick={() => {
              if (activeBreak?.StepId && currentLocation) {
                sendCompleteBreak(
                  { 
                    StepId: activeBreak.StepId, 
                    vehicleId: activeBreak.vehicleId || waypoint.vehicleId, 
                    routeId: activeBreak.routeId || waypoint.routeId 
                  }, 
                  currentLocation
                );
              }
            }}
          >
            {activeBreakTimeRemaining > 0 ? 'End Early' : 'End Break'}
          </button>
        </div>
      )}

      {/* Demand Response Phase-Based UI */}
      {demandResponseMode && currentPhase === 'hub_arrival' && (
        <div className='absolute inset-0 z-40 bg-white flex flex-col items-center justify-center p-4'>
          <div className='text-center mb-6'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-8 h-8 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
              </svg>
            </div>
            <h3 className='text-xl font-bold text-gray-900'>Arrived at Hub</h3>
          </div>
          <div className='flex flex-col gap-3 w-full max-w-xs'>
            {isCompletingArrivals ? (
              <button
                disabled
                className='w-full bg-blue-400 cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2'
              >
                <svg className='animate-spin h-5 w-5' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                </svg>
                Completing Trips...
              </button>
            ) : (
              <button
                onClick={onStartOutbound}
                className='w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors'
              >
                Start Outbound
              </button>
            )}
          </div>
        </div>
      )}

      {demandResponseMode && currentPhase === 'passenger_boarding' && !isEndStep && (
        <div className='absolute inset-0 z-40 bg-white flex flex-col items-center justify-center p-4'>
          <div className='text-center mb-4'>
            <h3 className='text-xl font-bold text-gray-900'>Passengers Boarding</h3>
          </div>
          <div className='flex items-center justify-center gap-4 mb-4'>
            <button
              onClick={() => setBoardingCount(prev => Math.max(0, prev - 1))}
              className='w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-xl font-bold text-white'
            >
              -
            </button>
            <input
              type='number'
              min='0'
              max='20'
              value={boardingCount}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10) || 0;
                setBoardingCount(Math.max(0, Math.min(20, val)));
              }}
              className='w-20 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg'
            />
            <button
              onClick={() => setBoardingCount(prev => Math.min(20, prev + 1))}
              className='w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-xl font-bold text-white'
            >
              +
            </button>
          </div>
          <button
            onClick={() => onConfirmBoarding(boardingCount)}
            className='w-full max-w-xs bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors'
          >
            Confirm Boarding ({boardingCount} passenger{boardingCount !== 1 ? 's' : ''})
          </button>
        </div>
      )}

      {demandResponseMode && currentPhase === 'outbound_dropoff' && (
        <div className='absolute inset-0 z-40 bg-white flex flex-col items-center justify-center p-4'>

          <div className='flex flex-col items-center gap-4 w-full max-w-xs'>
            {/* Large passenger count display */}
            <div className='text-6xl font-bold text-blue-600 mb-2'>
              {outboundPassengerCount}
            </div>
            {/* Record dropoff button */}
            {outboundPassengerCount > 0 && (
              <button
                onClick={onOpenFlagTripModal}
                className='w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2'
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
                </svg>
                Record Passenger Exit
              </button>
            )}
            {/* Manual "I'm at Hub" button for returning to hub */}
            <button
              onClick={onManualHubArrival}
              className='w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors text-sm'
            >
              Return to Hub
            </button>
          </div>
        </div>
      )}

 

      {/* Temporary pickup mode banner - shows outbound passenger count while handling a pickup */}
      {demandResponseMode && temporaryPickupMode && outboundPassengerCount > 0 && (
        <div className='absolute top-0 left-0 right-0 z-30 bg-orange-500 text-white py-2 px-4 flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
            </svg>
            <span className='font-semibold'>{outboundPassengerCount} passenger{outboundPassengerCount !== 1 ? 's' : ''} on vehicle</span>
          </div>
          <span className='text-sm opacity-90'>Handling pickup</span>
        </div>
      )}

      <div className='flex w-full '>
        {waypoint.type === 'pickup' && (
          <div
            className={`w-1 `}
            style={{ backgroundColor: waypoint.wheelChair ? 'rgb(66 186 240)' : 'rgb(255 210 5)',
              height: !isPhone ? '320px' : '180px' 
            }}
          />
        )}

        {/* main card content */}
        <div className={`flex flex-col w-full ${!isPhone ? 'h-64' : 'h-44'}`}>
          <div className={`flex flex-col px-3 ${!isPhone ? 'h-44 py-2' : 'h-32'}`}>
            {isStartStep ? (
              // Start step display
              <div className={`flex items-center justify-center h-full ${!isPhone ? 'my-4' : 'my-2'}`}>
                <div className='text-center'>
                  <h3 className={`font-semibold mb-2 text-green-600 ${!isPhone ? 'text-3xl' : 'text-2xl'}`}>
                    Route Start
                  </h3>
                  <p className={!isPhone ? 'text-xl' : 'text-lg'}>
                    Begin work at {waypoint.time || waypoint.scheduledTime || 'N/A'}
                  </p>
                </div>
              </div>
            ) : isEndStep ? (
              // End step display
              <div className={`flex items-center justify-center h-full ${!isPhone ? 'my-4' : 'my-2'}`}>
                <div className='text-center'>
                  <h3 className={`font-semibold mb-2 text-red-600 ${!isPhone ? 'text-3xl' : 'text-2xl'}`}>
                    Route End
                  </h3>
                  <p className={!isPhone ? 'text-xl' : 'text-lg'}>
                    Complete shift and submit
                  </p>
                </div>
              </div>
            ) : isBreak ? (
              // Break step display with toggle or split shift UI
              <div 
                className={`flex flex-col items-center justify-center h-full ${!isPhone ? 'my-4' : 'my-2'}`}
                style={isSplitShiftBreak || isSplitShiftApproved ? { backgroundColor: '#FFFACD', borderRadius: '8px', padding: !isPhone ? '12px' : '8px' } : 
                       isSplitShiftRequested ? { backgroundColor: '#FEF3C7', borderRadius: '8px', padding: !isPhone ? '12px' : '8px' } :
                       isSplitShiftRejected ? { backgroundColor: '#FEE2E2', borderRadius: '8px', padding: !isPhone ? '12px' : '8px' } : {}}
              >
                <h3 className={`font-semibold mb-2 ${!isPhone ? 'text-2xl' : 'text-xl'}`} style={
                  isSplitShiftBreak || isSplitShiftApproved ? { color: '#B8860B' } : 
                  isSplitShiftRequested ? { color: '#D97706' } :
                  isSplitShiftRejected ? { color: '#DC2626' } : {}
                }>
                  {isSplitShiftRequested ? 'Split Shift Pending' :
                   isSplitShiftApproved ? 'Split Shift Approved' :
                   isSplitShiftRejected ? 'Split Shift Declined' :
                   isSplitShiftBreak ? 'Split Shift Break' : 
                   (isOnBreak ? 'On Break' : 'Scheduled Break')}
                </h3>
                <p className={`text-gray-600 mb-1 ${!isPhone ? 'text-base' : 'text-sm'}`}>
                  Scheduled: {waypoint.time || waypoint.scheduledTime || 'N/A'}
                </p>
                <p className={`text-gray-600 mb-2 ${!isPhone ? 'text-base' : 'text-sm'}`}>
                  Duration: {Math.floor((waypoint.duration || waypoint.Duration || 1800) / 60)} minutes
                </p>
                {isSplitShiftRequested && (
                  <p className='text-sm text-amber-700 mb-1 font-medium animate-pulse'>
                    Waiting for dispatch approval...
                  </p>
                )}
                {isSplitShiftApproved && (
                  <p className='text-sm text-amber-700 mb-1 font-medium'>
                    Ready to clock out and resume route later
                  </p>
                )}
                {isSplitShiftRejected && (
                  <p className='text-sm text-red-700 mb-1 font-medium'>
                    Request declined - take regular break instead
                  </p>
                )}
                {isSplitShiftBreak && !isSplitShiftApproved && !isSplitShiftRequested && (
                  <p className='text-sm text-amber-700 mb-1 font-medium'>
                    Clock out and resume route later
                  </p>
                )}
                {isOnBreak && breakTimeRemaining > 0 && !isSplitShiftBreak && !isSplitShiftApproved && !isSplitShiftRequested && (
                  <p className={`font-bold text-blue-600 mb-2 ${!isPhone ? 'text-xl' : 'text-lg'}`}>
                    Time remaining: {Math.floor(breakTimeRemaining / 60)}:{String(breakTimeRemaining % 60).padStart(2, '0')}
                  </p>
                )}
              </div>
            ) : isHubStep ? (
              // Hub step display - green "Arriving at Hub" card with hub name and ETA
              <div className={`flex flex-col items-center justify-center h-full ${!isPhone ? 'my-4' : 'my-2'}`}>
                <div className={`bg-green-100 rounded-full flex items-center justify-center mb-3 ${!isPhone ? 'w-20 h-20' : 'w-16 h-16'}`}>
                  <svg className={`text-green-600 ${!isPhone ? 'w-12 h-12' : 'w-10 h-10'}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' />
                  </svg>
                </div>
                <h3 className={`font-bold text-green-700 mb-1 ${!isPhone ? 'text-2xl' : 'text-xl'}`}>
                  {waypoint.LandmarkName || 'Hub Location'}
                </h3>
                <p className={`text-green-600 ${!isPhone ? 'text-lg' : 'text-base'}`}>
                  ETA: {waypoint.eta ? new Date(waypoint.eta).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'N/A'}
                </p>
              </div>
            ) : demandResponseMode && (waypoint.type === 'delivery' || waypoint.type === 'dropoff') && !temporaryPickupMode ? (
              // Demand Response mode: Dropoff step shows "Heading to Hub" view
              <div className={`flex flex-col items-center justify-center h-full ${!isPhone ? 'my-4' : 'my-2'}`}>
                <div className={`bg-green-100 rounded-full flex items-center justify-center mb-3 ${!isPhone ? 'w-20 h-20' : 'w-16 h-16'}`}>
                  <svg className={`text-green-600 ${!isPhone ? 'w-12 h-12' : 'w-10 h-10'}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' />
                  </svg>
                </div>
                <h3 className={`font-bold text-green-700 mb-1 ${!isPhone ? 'text-2xl' : 'text-xl'}`}>
                  Heading to Hub
                </h3>
            
              </div>
            ) : (
                // Regular trip step display
                <>
                <div className={`flex flex-col ${!isPhone ? 'my-3' : 'my-1'}`}>
                  {/* Top line: avatar + Pick/Drop + wheelchair icon + Passenger Name (full width) */}
                  <div className='flex items-center gap-2 w-full overflow-hidden'>
                    <div
                      className={`flex-shrink-0 rounded-full flex items-center justify-center font-bold text-white ${!isPhone ? 'w-9 h-9 text-base' : 'w-7 h-7 text-sm'}`}
                      style={{ backgroundColor: '#3B5BDB' }}
                    >
                      {(waypoint.passengerName || '?').trim().charAt(0).toUpperCase()}
                    </div>
                    <h3 className={`font-bold overflow-hidden whitespace-nowrap text-ellipsis ${!isPhone ? 'text-2xl' : 'text-xl'}`}>
                      {waypoint.type === 'pickup' ? 'Pick:' : 'Drop:'} {waypoint.wheelChair && '🧑🏽‍🦽'} {waypoint.passengerName}
                    </h3>
                  </div>

                  {/* Address (left) + Times (right) */}
                  <div className='flex w-full'>
                    <div className='flex-1 min-w-0 flex flex-col'>
                      <p className={`font-semibold text-left overflow-hidden whitespace-nowrap text-ellipsis ${!isPhone ? 'text-xl' : 'text-lg'}`}>{waypoint.address}</p>
                      {waypoint.address2 && waypoint.address2 !== waypoint.address && (
                        <p className={`font-semibold text-left overflow-hidden whitespace-nowrap text-ellipsis ${!isPhone ? 'text-xl' : 'text-lg'}`}>{waypoint.address2}</p>
                      )}
                    </div>
                    <div className={`flex flex-col ml-2 flex-shrink-0 text-right whitespace-nowrap ${!isPhone ? 'text-lg' : 'text-base'}`}>
                      <div className='flex justify-end'>
                        <span className='mr-1'>Schd:</span>
                        <span>{formattedScheduledTime}</span>
                      </div>
                      <div className='flex justify-end'>
                        <span className='mr-1'>ETA:</span>
                        <span>{etaTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) || waypoint.scheduledTime || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment status — ALWAYS rendered, exactly two values. Sourced from the
                      server's payment block on reservation (db/util.js formatTrip), which
                      both assigned and flag trips carry, so no server change is needed.
                      See utils/tripPaymentStatus.js for the Paid/Unpaid mapping. */}
                  <div className='flex justify-end mt-1'>
                    <span
                      className={`inline-flex items-center rounded-full border px-3 py-1 font-semibold ${
                        !isPhone ? 'text-base' : 'text-sm'
                      } ${
                        isCardAccountTrip
                          ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                          : tripPaidStatus.isPaid
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : 'bg-orange-50 text-orange-700 border-orange-300'
                      }`}
                    >
                      {isCardAccountTrip ? 'Account Trip' : tripPaidStatus.label}
                    </span>
                  </div>
                </div>
 
                {comments ? (
                   <div className={`flex items-start ${!isPhone ? 'py-2 px-3 rounded-lg' : 'py-1 px-2 rounded'}`}
                     style={{ 
                       backgroundColor: 'rgb(219, 234, 254)',
                       minHeight: !isPhone ? '96px' : '32px'
                     }}>
                    <div className={`text-gray-700 overflow-hidden ${!isPhone ? 'text-lg' : 'text-sm'}`}
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: !isPhone ? 3 : 1,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.4'
                      }}>
                      <span className="font-bold">Comments: </span>{comments}
                    </div>
                    </div>
                  ) : (
                    <div style={{ minHeight: !isPhone ? '96px' : '32px' }} />
                  )}
                </>
              )}
              </div>
              <div className={`flex flex-col px-3 relative ${!isPhone ? 'h-14 py-2 mt-16' : 'h-11 mt-4'}`}>
              {isStartStep ? (
              // Start step: show Begin Work button
              <div className='flex space-x-2 w-full'>
                <button 
                  className={`text-white font-bold rounded w-full ${!isPhone ? 'py-3 px-4 text-xl' : 'py-2 px-4'}`}
                  style={{ backgroundColor: '#22c55e' }}
                  onClick={handleCompleteStart}
                >
                  Begin Work
                </button>
              </div>
            ) : isEndStep ? (
              // End step: show End Work button
              <div className='flex space-x-2 w-full'>
                <button 
                  className={`text-white font-bold rounded w-full ${!isPhone ? 'py-3 px-4 text-xl' : 'py-2 px-4'}`}
                  style={{ backgroundColor: '#ef4444' }}
                  onClick={handleCompleteEnd}
                >
                  End Work
                </button>
              </div>
            ) : isBreak ? (
              // Break step: show toggle switch OR split shift button based on status
              isSplitShiftBreak || isSplitShiftApproved ? (
                // Split shift break (approved or already marked): show Clock Out button
                <div className='flex space-x-2 w-full'>
                  <button 
                    className={`text-white font-bold rounded w-full ${!isPhone ? 'py-3 px-4 text-xl' : 'py-2 px-4'}`}
                    style={{ backgroundColor: '#B8860B' }}
                    onClick={handleCompleteSplitShiftBreak}
                  >
                    Clock Out (Resume Later)
                  </button>
                </div>
              ) : isSplitShiftRequested ? (
                // Split shift requested but waiting for approval: show waiting state
                <div className='flex items-center justify-center w-full'>
                  <span className='text-amber-700 font-medium animate-pulse'>
                    Awaiting dispatch approval...
                  </span>
                </div>
              ) : isSplitShiftRejected ? (
                // Split shift rejected: show regular break toggle
                <div className='flex items-center justify-center w-full'>
                  <span className={`mr-3 text-sm font-medium ${!isOnBreak ? 'text-gray-900' : 'text-gray-400'}`}>
                    Working
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={isOnBreak}
                      onChange={(e) => handleBreakToggle(e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className={`ml-3 text-sm font-medium ${isOnBreak ? 'text-blue-600' : 'text-gray-400'}`}>
                    On Break
                  </span>
                </div>
              ) : (
                // Regular break: show toggle switch
                <div className='flex items-center justify-center w-full'>
                  <span className={`mr-3 text-sm font-medium ${!isOnBreak ? 'text-gray-900' : 'text-gray-400'}`}>
                    Working
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={isOnBreak}
                      onChange={(e) => handleBreakToggle(e.target.checked)}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className={`ml-3 text-sm font-medium ${isOnBreak ? 'text-blue-600' : 'text-gray-400'}`}>
                    On Break
                  </span>
                </div>
              )
            ) : isHubStep ? (
              // Hub step: show "I'm at Hub" button
              <div className='flex w-full'>
                <button 
                  className={`text-white font-bold rounded flex-1 bg-green-600 hover:bg-green-700 ${!isPhone ? 'py-3 px-4 text-xl' : 'py-2 px-4'}`}
                  onClick={onManualHubArrival}
                >
                  I&apos;m at Hub
                </button>
              </div>
            ) : waypoint.type === 'pickup' ? (
              // Pickup step: Navigate (outlined) + Arrive (filled) button row
              <div className='flex space-x-2 w-full'>
                <button
                  className={`font-bold rounded-full border-2 flex items-center justify-center gap-1.5 ${!isPhone ? 'py-2 px-4 text-lg' : 'py-1.5 px-3 text-sm'}`}
                  style={{ borderColor: '#3B5BDB', color: '#3B5BDB', backgroundColor: '#fff' }}
                  onClick={handleMapDirections}
                  title="Open in Maps"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={!isPhone ? "h-5 w-5" : "h-4 w-4"} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Navigate
                </button>

                {/* Arrive Button */}
                <button
                  className={`text-white font-bold rounded-full flex-1 ${!isPhone ? 'py-2 px-3 text-xl' : 'py-1.5 px-2'}`}
                  style={{ backgroundColor: '#3B5BDB' }}
                  onClick={handleArriveOrigin}
                >
                  Arrived
                </button>
              </div>
            ) : waypoint.type === 'delivery' || waypoint.type === 'dropoff' ? (
              // Delivery step: in demand response mode show "I'm at Hub", otherwise show normal Arrive/Complete
              demandResponseMode && !temporaryPickupMode ? (
                <div className='flex w-full'>
                  <button
                    className={`text-white font-bold rounded-full flex-1 bg-green-600 hover:bg-green-700 ${!isPhone ? 'py-3 px-4 text-xl' : 'py-2 px-4'}`}
                    onClick={onManualHubArrival}
                  >
                    I&apos;m at Hub
                  </button>
                </div>
              ) : (
                <div className='flex space-x-2 w-full'>
                  <button
                    className={`font-bold rounded-full border-2 flex items-center justify-center gap-1.5 ${!isPhone ? 'py-2 px-4 text-lg' : 'py-1.5 px-3 text-sm'}`}
                    style={{ borderColor: '#3B5BDB', color: '#3B5BDB', backgroundColor: '#fff' }}
                    onClick={handleMapDirections}
                    title="Open in Maps"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className={!isPhone ? "h-5 w-5" : "h-4 w-4"} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Navigate
                  </button>

                  {/* Arrive/Complete Button - changes based on arrival status */}
                  {!isArrivedDestination ? (
                    <button
                      className={`text-white font-bold rounded-full flex-1 ${!isPhone ? 'py-2 px-3 text-xl' : 'py-1.5 px-2'}`}
                      style={{ backgroundColor: '#3B5BDB' }}
                      onClick={handleArriveDestination}
                    >
                      Arrived
                    </button>
                  ) : (
                    <button
                      className={`text-white font-bold rounded-full flex-1 ${!isPhone ? 'py-2 px-3 text-xl' : 'py-1.5 px-2'}`}
                      style={{ backgroundColor: '#3B5BDB' }}
                      onClick={handleComplete}
                    >
                      Complete
                    </button>
                  )}
                </div>
              )
            ) : null}
          </div>
        </div>

        {waypoint.type === 'dropoff' && (
          <div
            className='w-1'
            style={{ backgroundColor: waypoint.wheelChair ? 'rgb(66 186 240)' : 'rgb(255 210 5)' }}
          />
        )}

      </div>

      {/* Pickup Arrival Modal - shows after arriving at pickup */}
      <PickupArrivalModal
        isOpen={showPickupArrivalModal}
        onClose={() => setShowPickupArrivalModal(false)}
        // The MERGED block, not the raw reservation: a settlement that arrived live in
        // this session (trip:paymentSettled, an acknowledged cash payment) is only in
        // the override, so the modal's payment chip would otherwise still show the
        // pre-payment arrangement while the card behind it already reads Paid.
        waypoint={
          cardPaymentBlock === cardReservation
            ? waypoint
            : { ...waypoint, trip: { ...(waypoint.trip || {}), reservation: cardPaymentBlock } }
        }
        onLoad={handleLoad}
        onNoShow={handleNoShow}
        onSignature={handleSignatureSave}
        onCopayConfirm={handleCopayConfirm}
        onCancel={handleModalCancel}
      />
    </div>
  );
};

export default ItineraryCurrentWaypoint;
